<script setup lang="ts">
import { computed } from 'vue'
import SubstandardBullets from '@/standards/SubstandardBullets.vue'
import { fieldText } from '@/standards/standard_fields'
import type { StandardDetail } from '@/standards/standard_types'
import type { ListMarkerType } from '@/standards/list_markers'

const props = withDefaults(
  defineProps<{
    setCode: string
    shortRef: string
    standardText: string
    standardInfo?: Partial<StandardDetail>
  }>(),
  {
    standardInfo: () => ({})
  }
)

const SUBSTANDARD_BULLET_FIELD_BY_SET_CODE = new Map([
  ['CCSS', 'standard_component'],
  ['CA-ELD', 'component'],
  ['NCSCoS', 'substandard_let'],
  ['NYSNGLS', 'substandard_let'],
  ['OLS', 'substandard_let'],
  ['TEKS-2017', 'standard_roman_numeral']
])

const SUBSTANDARD_LIST_TYPE_BY_SET_CODE = new Map<string, ListMarkerType>([['TEKS-2017', 'i']])

const SET_CODES_WITH_SUBSTANDARD_IN_STANDARD_TEXT = new Set(['CA-ELD', 'CCSS', 'CPLF', 'TEKS', 'TEKS-2017', 'TPKG'])

const standardInfoField = (field: string) => fieldText(props.standardInfo, field)

const standardGroup = computed(() => (props.setCode === 'SCCCR' ? standardInfoField('standard_group') : ''))

const standardHeading = computed(() => (standardGroup.value ? `${props.shortRef} ${standardGroup.value}` : props.shortRef))

const substandardBullet = computed(() =>
  standardInfoField(SUBSTANDARD_BULLET_FIELD_BY_SET_CODE.get(props.setCode) ?? 'substandard')
)

const substandardListType = computed(() => SUBSTANDARD_LIST_TYPE_BY_SET_CODE.get(props.setCode) ?? 'a')

const substandardText = computed(() =>
  standardInfoField(SET_CODES_WITH_SUBSTANDARD_IN_STANDARD_TEXT.has(props.setCode) ? 'standard_text' : 'substandard_text')
)

const isStandardItsOwnSubstandard = computed(() => Boolean(substandardBullet.value) && props.standardText === substandardText.value)
</script>

<template>
  <div class="text-sm leading-5" data-testid="present-levels-standard-view">
    <div class="py-1">
      <b>{{ standardHeading }}</b><br v-if="standardGroup" /><template v-else>{{ ' ' }}</template><span v-html="standardText" />
    </div>
    <SubstandardBullets
      v-if="!isStandardItsOwnSubstandard"
      :ordered-substandards="standardInfo.substandards"
      :ol-type="substandardListType"
      :single-substandard-bullet="substandardBullet"
      :single-substandard-text="substandardText"
    />
  </div>
</template>
